
import { TeamsPhotoPost, TeamsPhotoComment, TeamsChannel } from '../types';

/**
 * Teams Photo Service for Microsoft Teams integration
 * 
 * This service handles posting photos to Teams channels, managing photo posts,
 * and interacting with Microsoft Graph API for Teams functionality.
 * 
 * Usage:
 * ```typescript
 * // Create an instance with Azure AD service
 * const azureADService = new AzureADAuthService(authService, 'web');
 * const teamsPhotoService = new TeamsPhotoService(azureADService);
 * 
 * // Set the access token before making API calls
 * teamsPhotoService.setAccessToken('your-access-token-here');
 * 
 * // Post a photo to Teams
 * const photoPost = await teamsPhotoService.postPhotoToTeams(
 *   'challenge-123',
 *   photoFile,
 *   'Great workout!'
 * );
 * ```
 * 
 * Note: The access token must be set using setAccessToken() before making any API calls,
 * as the shared library AzureADAuthService doesn't store tokens.
 */
export class TeamsPhotoService {
  private static instance: TeamsPhotoService;
  private currentAccessToken: string | null = null;

  private constructor() {
    // No Azure AD service dependency needed for basic functionality
    // The calling application will provide the access token
  }

  /**
   * Get singleton instance for backward compatibility
   */
  public static getInstance(): TeamsPhotoService {
    if (!TeamsPhotoService.instance) {
      TeamsPhotoService.instance = new TeamsPhotoService();
    }
    return TeamsPhotoService.instance;
  }

  /**
   * Set the access token for Teams API calls
   */
  public setAccessToken(accessToken: string): void {
    this.currentAccessToken = accessToken;
  }

  /**
   * Get the current access token
   */
  private async getAccessToken(): Promise<string | null> {
    // First check if we have a manually set token
    if (this.currentAccessToken) {
      return this.currentAccessToken;
    }

    // Try to get token from sessionStorage (web app)
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const tokenData = window.sessionStorage.getItem('azuread.tokens');
        if (tokenData) {
          const tokens = JSON.parse(tokenData);

          // Check if token is still valid
          if (tokens.expires_in && tokens.timestamp) {
            const expiresOnMs = tokens.timestamp + (tokens.expires_in * 1000);
            const currentTimeMs = Date.now();

            if (expiresOnMs > currentTimeMs) {
              console.log('[TeamsPhotoService] Using access token from sessionStorage');
              return tokens.access_token;
            } else {
              console.log('[TeamsPhotoService] Token from sessionStorage is expired');
            }
          }
        }
      }
    } catch (error) {
      console.warn('[TeamsPhotoService] Error getting token from sessionStorage:', error);
    }

    // Try to get token from mobile storage (React Native)
    try {
      if (typeof global !== 'undefined' && global.require) {
        // This is React Native environment
        const AsyncStorage = global.require('@react-native-async-storage/async-storage').default;
        if (AsyncStorage) {
          const tokenData = await AsyncStorage.getItem('azuread.tokens');
          if (tokenData) {
            const tokens = JSON.parse(tokenData);

            // Check if token is still valid
            if (tokens.expires_in && tokens.timestamp) {
              const expiresOnMs = tokens.timestamp + (tokens.expires_in * 1000);
              const currentTimeMs = Date.now();

              if (expiresOnMs > currentTimeMs) {
                console.log('[TeamsPhotoService] Using access token from AsyncStorage');
                return tokens.access_token;
              } else {
                console.log('[TeamsPhotoService] Token from AsyncStorage is expired');
              }
            }
          }
        }
      }
    } catch (error) {
      console.warn('[TeamsPhotoService] Error getting token from AsyncStorage:', error);
    }

    // No valid token found
    console.warn('No access token available. Please sign in with Azure AD or call setAccessToken() before making Teams API calls.');
    return null;
  }

  /**
   * Post a photo to a Teams channel
   */
  async postPhotoToTeams(
    challengeId: string,
    photoFile: File | Blob,
    caption?: string | null,
    channelId?: string
  ): Promise<TeamsPhotoPost> {
    try {
      // Get access token for Microsoft Graph
      const accessToken = await this.getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available for Teams integration');
      }

      // Get or create Teams channel for the challenge
      const channel = await this.getOrCreateChallengeChannel(challengeId, accessToken);

      // Upload photo to OneDrive/SharePoint
      const photoUrl = await this.uploadPhotoToOneDrive(photoFile, challengeId, accessToken);

      // Post message with photo to Teams channel
      const teamsMessage = await this.postMessageToTeams(
        channel.id,
        photoUrl,
        caption,
        accessToken
      );

      // Create photo post record
      const photoPost: TeamsPhotoPost = {
        id: this.generatePhotoId(),
        challengeId,
        userId: await this.getCurrentUserId(accessToken),
        userDisplayName: await this.getCurrentUserDisplayName(accessToken),
        photoUrl,
        teamsMessageId: teamsMessage.id,
        teamsChannelId: channel.id,
        caption: caption || null, // Ensure caption is never undefined
        postedAt: new Date().toISOString(),
        likes: 0,
        comments: []
      };

      return photoPost;
    } catch (error) {
      console.error('Error posting photo to Teams:', error);
      throw new Error(`Failed to post photo to Teams: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get photos for a specific challenge from Teams
   */
  async getChallengePhotos(challengeId: string): Promise<TeamsPhotoPost[]> {
    try {
      const accessToken = await this.getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available for Teams integration');
      }

      // Get challenge channel
      const channel = await this.getChallengeChannel(challengeId, accessToken);
      if (!channel) {
        return [];
      }

      // Get messages from Teams channel
      const messages = await this.getChannelMessages(channel.id, accessToken);

      // Filter and parse photo messages
      const photoPosts = messages
        .filter(msg => msg.content?.includes('photo') || msg.content?.includes('image'))
        .map(msg => this.parsePhotoMessage(msg, challengeId))
        .filter(Boolean) as TeamsPhotoPost[];

      return photoPosts;
    } catch (error) {
      console.error('Error getting challenge photos:', error);
      throw new Error(`Failed to get challenge photos: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get available Teams channels
   */
  async getTeamsChannels(): Promise<TeamsChannel[]> {
    try {
      const accessToken = await this.getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available for Teams integration');
      }

      const response = await fetch('https://graph.microsoft.com/v1.0/me/joinedTeams', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to get teams: ${response.status} ${response.statusText}`);
      }

      const teamsData = await response.json();
      const channels: TeamsChannel[] = [];

      // Get channels for each team
      for (const team of teamsData.value) {
        const channelsResponse = await fetch(`https://graph.microsoft.com/v1.0/teams/${team.id}/channels`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (channelsResponse.ok) {
          const channelsData = await channelsResponse.json();
          channels.push(...channelsData.value.map((ch: any) => ({
            id: ch.id,
            displayName: ch.displayName,
            description: ch.description,
            webUrl: ch.webUrl
          })));
        }
      }

      return channels;
    } catch (error) {
      console.error('Error getting Teams channels:', error);
      throw new Error(`Failed to get Teams channels: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Like a photo post
   */
  async likePhoto(photoId: string): Promise<void> {
    try {
      const accessToken = await this.getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available for Teams integration');
      }

      // This would typically update the Teams message reactions
      // For now, we'll just return success
      console.log(`Photo ${photoId} liked successfully`);
    } catch (error) {
      console.error('Error liking photo:', error);
      throw new Error(`Failed to like photo: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Add comment to a photo post
   */
  async addComment(photoId: string, comment: string): Promise<TeamsPhotoComment> {
    try {
      const accessToken = await this.getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available for Teams integration');
      }

      // This would typically add a reply to the Teams message
      // For now, we'll just return a mock comment
      const newComment: TeamsPhotoComment = {
        id: this.generateCommentId(),
        userId: await this.getCurrentUserId(accessToken),
        userDisplayName: await this.getCurrentUserDisplayName(accessToken),
        comment,
        postedAt: new Date().toISOString()
      };

      return newComment;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw new Error(`Failed to add comment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Delete a photo from OneDrive
   */
  async deletePhotoFromOneDrive(oneDriveItemId: string, accessToken: string): Promise<void> {
    try {
      const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${oneDriveItemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to delete file: ${response.status} ${response.statusText}`);
      }

      console.log(`Photo deleted from OneDrive: ${oneDriveItemId}`);
    } catch (error) {
      console.error('Error deleting photo from OneDrive:', error);
      throw new Error(`Failed to delete photo from OneDrive: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Private helper methods

  private async getOrCreateChallengeChannel(challengeId: string, accessToken: string): Promise<TeamsChannel> {
    // Try to find existing channel for this challenge
    const existingChannel = await this.getChallengeChannel(challengeId, accessToken);
    if (existingChannel) {
      return existingChannel;
    }

    // Create new channel for the challenge
    return await this.createChallengeChannel(challengeId, accessToken);
  }

  private async getChallengeChannel(challengeId: string, accessToken: string): Promise<TeamsChannel | null> {
    try {
      // This would search for a channel with the challenge ID in its name or description
      // For now, return null to trigger channel creation
      return null;
    } catch (error) {
      console.error('Error getting challenge channel:', error);
      return null;
    }
  }

  private async createChallengeChannel(challengeId: string, accessToken: string): Promise<TeamsChannel> {
    try {
      // This would create a new Teams channel for the challenge
      // For now, return a mock channel
      return {
        id: `challenge-${challengeId}`,
        displayName: `Challenge ${challengeId}`,
        description: `Photo sharing channel for challenge ${challengeId}`,
        webUrl: `https://teams.microsoft.com/challenge-${challengeId}`
      };
    } catch (error) {
      console.error('Error creating challenge channel:', error);
      throw new Error('Failed to create Teams channel');
    }
  }

  private async uploadPhotoToOneDrive(photoFile: File | Blob, challengeId: string, accessToken: string): Promise<string> {
    try {
      // This would upload the photo to OneDrive/SharePoint
      // For now, return a mock URL
      return `https://onedrive.live.com/challenge-${challengeId}/photo-${Date.now()}.jpg`;
    } catch (error) {
      console.error('Error uploading photo to OneDrive:', error);
      throw new Error('Failed to upload photo to OneDrive');
    }
  }

  private async postMessageToTeams(
    channelId: string,
    photoUrl: string,
    caption: string | null | undefined,
    accessToken: string
  ): Promise<{ id: string }> {
    try {
      // This would post a message with the photo to the Teams channel
      // For now, return a mock message ID
      return { id: `msg-${Date.now()}` };
    } catch (error) {
      console.error('Error posting message to Teams:', error);
      throw new Error('Failed to post message to Teams');
    }
  }

  private async getChannelMessages(channelId: string, accessToken: string): Promise<any[]> {
    try {
      // This would get messages from the Teams channel
      // For now, return empty array
      return [];
    } catch (error) {
      console.error('Error getting channel messages:', error);
      return [];
    }
  }

  private parsePhotoMessage(message: any, challengeId: string): TeamsPhotoPost | null {
    try {
      // This would parse Teams messages to extract photo information
      // For now, return null
      return null;
    } catch (error) {
      console.error('Error parsing photo message:', error);
      return null;
    }
  }

  private async getCurrentUserId(accessToken: string): Promise<string> {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get current user info');
      }

      const userData = await response.json();
      return userData.id;
    } catch (error) {
      console.error('Error getting current user ID:', error);
      return 'unknown-user';
    }
  }

  private async getCurrentUserDisplayName(accessToken: string): Promise<string> {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get current user info');
      }

      const userData = await response.json();
      return userData.displayName || 'Unknown User';
    } catch (error) {
      console.error('Error getting current user display name:', error);
      return 'Unknown User';
    }
  }

  private generatePhotoId(): string {
    return `photo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateCommentId(): string {
    return `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
